import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";

interface RotatingWordProps {
  /** Liste des mots à afficher en boucle. */
  words: string[];
  /** Vitesse de frappe — temps entre deux lettres ajoutées (ms). */
  typeSpeed?: number;
  /** Vitesse d'effacement — temps entre deux lettres retirées (ms). */
  deleteSpeed?: number;
  /** Pause après que le mot soit complètement écrit (ms). */
  holdAfterType?: number;
  /** Pause après que le mot soit complètement effacé, avant le suivant (ms). */
  holdAfterDelete?: number;
  /** @deprecated — conservé pour compatibilité, non utilisé. */
  fastInterval?: number;
  /** @deprecated — conservé pour compatibilité, non utilisé. */
  slowInterval?: number;
  /** @deprecated — conservé pour compatibilité, non utilisé. */
  highlight?: string[];
  /** @deprecated — conservé pour compatibilité, non utilisé. */
  transitionDuration?: number;
  /** Classe optionnelle pour styliser le mot. */
  className?: string;
  /** Direction du texte (rtl pour l'arabe). */
  dir?: "ltr" | "rtl";
}

type Phase = "typing" | "holding" | "deleting" | "pausing";

/**
 * Effet machine à écrire : chaque mot s'écrit lettre par lettre,
 * marque une pause, puis s'efface, et on passe au suivant en boucle.
 */
export const RotatingWord = ({
  words,
  typeSpeed = 70,
  deleteSpeed = 40,
  holdAfterType = 900,
  holdAfterDelete = 200,
  className = "",
  dir,
}: RotatingWordProps) => {
  const reduce = useReducedMotion();
  const [wordIdx, setWordIdx] = useState(0);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<Phase>("typing");

  useEffect(() => {
    if (words.length === 0) return;
    const current = words[wordIdx % words.length] ?? "";

    // Mode reduced motion : on affiche le mot complet et on alterne lentement.
    if (reduce) {
      setText(current);
      const id = setTimeout(() => {
        setWordIdx((i) => (i + 1) % words.length);
      }, 2000);
      return () => clearTimeout(id);
    }

    let delay = typeSpeed;

    if (phase === "typing") {
      if (text.length < current.length) {
        delay = typeSpeed;
        const id = setTimeout(() => setText(current.slice(0, text.length + 1)), delay);
        return () => clearTimeout(id);
      }
      const id = setTimeout(() => setPhase("holding"), 0);
      return () => clearTimeout(id);
    }

    if (phase === "holding") {
      const id = setTimeout(() => setPhase("deleting"), holdAfterType);
      return () => clearTimeout(id);
    }

    if (phase === "deleting") {
      if (text.length > 0) {
        delay = deleteSpeed;
        const id = setTimeout(() => setText(current.slice(0, text.length - 1)), delay);
        return () => clearTimeout(id);
      }
      const id = setTimeout(() => setPhase("pausing"), 0);
      return () => clearTimeout(id);
    }

    if (phase === "pausing") {
      const id = setTimeout(() => {
        setWordIdx((i) => (i + 1) % words.length);
        setPhase("typing");
      }, holdAfterDelete);
      return () => clearTimeout(id);
    }
  }, [text, phase, wordIdx, words, typeSpeed, deleteSpeed, holdAfterType, holdAfterDelete, reduce]);

  const longest = words.reduce((a, b) => (a.length >= b.length ? a : b), "");

  return (
    <span
      dir={dir}
      className="relative inline-grid align-baseline"
      style={{ verticalAlign: "baseline" }}
    >
      {/* Spacer invisible : réserve la largeur du mot le plus long */}
      <span aria-hidden className={`invisible col-start-1 row-start-1 whitespace-nowrap ${className}`}>
        {longest}
      </span>

      <span className={`col-start-1 row-start-1 whitespace-nowrap ${className}`}>
        {text}
        <span
          aria-hidden
          className="ml-0.5 inline-block w-[0.08em] h-[0.9em] -mb-[0.05em] bg-current align-middle animate-pulse"
        />
      </span>

      {/* Lecture accessible */}
      <span className="sr-only">{words.join(", ")}</span>
    </span>
  );
};
